import React, { useState } from 'react';
import { Box, Button, FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { useToast } from '../contexts/ToastContext';
import {
  ProductSubmissionAdmin,
  ProductSubmissionStatus,
  SubmissionAnswerField,
  productSubmissionsApi,
} from '../services/productSubmissionsApi';
import { SUBMISSION_TONE } from '../components/placeholders/ProductSubmissionsCard';
import { tokens } from '../theme/tokens';
import { EmptyState, FormDialog, LoadingState, PageContainer, PageHeader, SectionCard, StatusChip } from '../components/ui';

const STATUSES: Array<ProductSubmissionStatus | ''> = ['', 'in_review', 'needs_answer', 'approved', 'promoted', 'rejected'];
const FIELDS: Array<SubmissionAnswerField | ''> = ['', 'name', 'model', 'upc', 'mpn', 'description', 'launchDate'];

/**
 * Admin shell (Alex), Catalog: products stores and vendors submitted (flag
 * `product_submissions`). Each is an ordinary staged row the QA queue enriches,
 * verifies and promotes; this page lists them with the submitter and lets
 * Motzu ask the submitter what enrichment needs. Promotion happens on the
 * Staged Products page, as for any row.
 */
const AdminProductSubmissionsPage: React.FC = () => {
  const { t } = useAppTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<ProductSubmissionStatus | ''>('');
  const [asking, setAsking] = useState<ProductSubmissionAdmin | null>(null);
  const [question, setQuestion] = useState('');
  const [field, setField] = useState<SubmissionAnswerField | ''>('');

  const [cursor, setCursor] = useState<string | null>(null);
  const [earlier, setEarlier] = useState<ProductSubmissionAdmin[]>([]);
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['admin-product-submissions', status, cursor],
    queryFn: () => productSubmissionsApi.listAdmin(status, cursor),
  });
  const changeStatus = (next: ProductSubmissionStatus | '') => {
    setStatus(next);
    setCursor(null);
    setEarlier([]);
  };
  const loadMore = () => {
    if (!data?.nextCursor) return;
    setEarlier((prev) => [...prev, ...(data.items || [])]);
    setCursor(data.nextCursor);
  };

  const ask = useMutation({
    mutationFn: ({ id, text, answerField }: { id: string; text: string; answerField: SubmissionAnswerField | null }) =>
      productSubmissionsApi.ask(id, text, answerField),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-product-submissions'] });
      setAsking(null);
      setQuestion('');
      setField('');
      showToast(t('admin.productSubmissions.asked'), 'success');
    },
    onError: (error: unknown) => showToast(error instanceof Error ? error.message : t('admin.productSubmissions.askFailed'), 'error'),
  });

  const rows = [...earlier, ...(data?.items || [])];

  return (
    <PageContainer>
      <PageHeader title={t('admin.productSubmissions.title')} description={t('admin.productSubmissions.description')} />
      <SectionCard
        title={t('admin.productSubmissions.listTitle')}
        actions={
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="product-submissions-status">{t('admin.productSubmissions.statusFilter')}</InputLabel>
            <Select
              labelId="product-submissions-status"
              label={t('admin.productSubmissions.statusFilter')}
              value={status}
              onChange={(event) => changeStatus(event.target.value as ProductSubmissionStatus | '')}
            >
              {STATUSES.map((value) => (
                <MenuItem key={value || 'all'} value={value}>
                  {value ? t(`store.productSubmissions.status.${value}`) : t('admin.productSubmissions.allStatuses')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        }
      >
        {isLoading && <LoadingState />}
        {!isLoading && rows.length === 0 && (
          <EmptyState size="compact" title={t('admin.productSubmissions.empty')} />
        )}
        {rows.map((submission) => (
          <Box key={submission.id} sx={{ py: 1.25, borderTop: `1px solid ${tokens.line.hairline}` }} data-testid="admin-product-submission">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Box sx={{ flex: 1, minWidth: 220 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {submission.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {[
                    submission.brand,
                    submission.model,
                    submission.sku ? `SKU ${submission.sku}` : null,
                    submission.upc ? `UPC ${submission.upc}` : null,
                    // Every organization that submitted the product, first submitter first (MOT-737).
                    (submission.submitters?.length
                      ? submission.submitters
                      : [{ organizationId: submission.submittedByOrganizationId, organizationName: submission.submittedByOrganizationName, kind: submission.kind }])
                      .map((s) => t(`admin.productSubmissions.kind.${s.kind}`, { name: s.organizationName || s.organizationId }))
                      .join(', '),
                    submission.submittedAt ? new Date(submission.submittedAt).toLocaleDateString() : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </Typography>
              </Box>
              <StatusChip label={t(`store.productSubmissions.status.${submission.status}`)} tone={SUBMISSION_TONE[submission.status]} size="small" />
              <StatusChip label={submission.stagingStatus} tone="neutral" size="small" variant="outlined" />
              <Button size="small" component={RouterLink} to={`/app/admin/staged-products?id=${encodeURIComponent(submission.id)}`}>
                {t('admin.productSubmissions.openStaged')}
              </Button>
              {submission.status !== 'promoted' && submission.status !== 'rejected' && (
                <Button size="small" variant="outlined" onClick={() => setAsking(submission)}>
                  {t('admin.productSubmissions.ask')}
                </Button>
              )}
            </Box>
            {submission.questions.length > 0 && (
              <Stack spacing={0.5} sx={{ mt: 1, pl: { sm: 2 } }}>
                {submission.questions.map((q) => (
                  <Typography key={q.id} variant="caption" color="text.secondary">
                    {q.question}
                    {' — '}
                    {q.answer ? q.answer : <em>{t('admin.productSubmissions.unanswered')}</em>}
                  </Typography>
                ))}
              </Stack>
            )}
          </Box>
        ))}
        {data?.nextCursor && (
          <Box sx={{ pt: 1.5 }}>
            <Button size="small" variant="outlined" disabled={isFetching} onClick={loadMore}>
              {t('admin.productSubmissions.loadMore')}
            </Button>
          </Box>
        )}
      </SectionCard>

      <FormDialog
        open={Boolean(asking)}
        title={t('admin.productSubmissions.askTitle', { name: asking?.name || '' })}
        onClose={() => setAsking(null)}
        onSubmit={() => asking && ask.mutate({ id: asking.id, text: question.trim(), answerField: field || null })}
        submitLabel={t('admin.productSubmissions.ask')}
        cancelLabel={t('common.cancel')}
        submitDisabled={!question.trim()}
        loading={ask.isPending}
      >
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label={t('admin.productSubmissions.questionLabel')}
            value={question}
            onChange={(event) => setQuestion(event.target.value)}
            multiline
            minRows={2}
            inputProps={{ maxLength: 500 }}
            autoFocus
          />
          <FormControl size="small">
            <InputLabel id="product-submissions-field">{t('admin.productSubmissions.fieldLabel')}</InputLabel>
            <Select
              labelId="product-submissions-field"
              label={t('admin.productSubmissions.fieldLabel')}
              value={field}
              onChange={(event) => setField(event.target.value as SubmissionAnswerField | '')}
            >
              {FIELDS.map((value) => (
                <MenuItem key={value || 'none'} value={value}>
                  {value ? t(`admin.productSubmissions.field.${value}`) : t('admin.productSubmissions.fieldNone')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary">{t('admin.productSubmissions.fieldHelp')}</Typography>
        </Stack>
      </FormDialog>
    </PageContainer>
  );
};

export default AdminProductSubmissionsPage;
